import React, { useEffect, useState } from 'react';

import SanitizedHTML from 'components/ui/sanitized_html';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInboxIn, faInboxOut, faMinus } from '@fortawesome/pro-solid-svg-icons';

import Attachments from 'bundles/apex/activities/attachments';
import LoadingSpinner from 'components/ui/loading_spinner';
import { get } from 'helpers/api';
import { formatDateTime } from 'helpers/formatters/date_time_formats';

import type { Email } from 'bundles/apex/activities/types';
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons';

// Layout tags plus the semantic formatting tags produced by the compose editor (Trix) and common
// in synced email HTML. Kept to benign formatting only — no script/style/img.
const EMAIL_BODY_TAGS = [
  'a', 'span', 'div', 'p', 'br', 'hr',
  'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'del',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'blockquote', 'pre', 'code',
  'ul', 'ol', 'li',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
];

interface Props {
  autoExpand?: boolean;
  email: Email;
}

export default function ActivityEmailThreadEmail({ autoExpand, email }: Props) {
  const [body, setBody] = useState<string | null>(null);
  const [showBody, setShowBody] = useState(false);

  useEffect(() => {
    if (autoExpand) {
      toggleBody();
    }
  }, []);

  async function toggleBody() {
    if (showBody) {
      setShowBody(false);
      return;
    }

    setShowBody(true);

    if (!body) {
      const { body } = await get(email.path);
      setBody(body);
    }
  }

  return (
    <div className="border rounded mb-3 bg-white">
      <div className="d-flex align-items-center gap-2 p-3 pe-4 clickable justify-content-between" onClick={toggleBody}>
        <div className="d-flex flex-column">
          <div className="d-flex text-muted fs-small">
            <FontAwesomeIcon className="pe-2" icon={email.sentByUser ? faInboxOut : faInboxIn} />
            {email.sentByUser ? 'Sent' : 'Received'} at {formatDateTime(email.timestamp)}
          </div>

          <div>
            {email.subject}
          </div>
        </div>

        <FontAwesomeIcon className="text-primary" icon={showBody ? faMinus : faPlusCircle} />
      </div>

      {showBody && (
        <div className="border-top border-light p-3">
          {body && <>
            <SanitizedHTML allowedTags={EMAIL_BODY_TAGS} html={body} />
            <Attachments attachments={email.attachments} bordered={false} />
          </>}
          {!body && <LoadingSpinner/>}
        </div>
      )}
    </div>
  );
}
